import React, { useState, useMemo } from 'react';
import type { AdminContentItem } from '../../lib/types';

interface LovePhrasesSectionProps {
  phrases: AdminContentItem[];
  onAddPhrase: (phrase: string, language: string) => Promise<void>;
  onUpdatePhrase: (id: string, phrase: string, language: string, isActive: boolean) => Promise<void>;
  onDeletePhrase: (id: string) => Promise<void>;
}

export const LovePhrasesSection: React.FC<LovePhrasesSectionProps> = ({
  phrases,
  onAddPhrase,
  onUpdatePhrase,
  onDeletePhrase,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');

  // Add new phrase form state
  const [newPhrase, setNewPhrase] = useState('');
  const [newLanguage, setNewLanguage] = useState('English');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPhrase, setEditPhrase] = useState('');
  const [editLanguage, setEditLanguage] = useState('');

  // Extract unique languages
  const availableLanguages = useMemo(() => {
    const set = new Set<string>();
    for (const p of phrases) {
      if (p.language) set.add(p.language);
    }
    return Array.from(set).sort();
  }, [phrases]);

  // Filtered list
  const filteredPhrases = useMemo(() => {
    return phrases.filter(p => {
      const textMatch = !searchTerm || (p.phrase || '').toLowerCase().includes(searchTerm.toLowerCase());
      const langMatch = languageFilter === 'all' || p.language === languageFilter;
      return textMatch && langMatch;
    });
  }, [phrases, searchTerm, languageFilter]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmedPhrase = newPhrase.trim();
    if (!trimmedPhrase) {
      setFormError('Phrase text is required');
      return;
    }

    // Check duplicate
    const isDup = phrases.some(p => (p.phrase || '').toLowerCase() === trimmedPhrase.toLowerCase());
    if (isDup) {
      setFormError('This phrase already exists in the collection');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddPhrase(trimmedPhrase, newLanguage.trim() || 'English');
      setNewPhrase('');
      setNewLanguage('English');
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to add phrase');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (item: AdminContentItem) => {
    setEditingId(item.id);
    setEditPhrase(item.phrase || '');
    setEditLanguage(item.language || 'English');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (id: string, isActive: boolean) => {
    const trimmed = editPhrase.trim();
    if (!trimmed) return;
    try {
      await onUpdatePhrase(id, trimmed, editLanguage.trim() || 'English', isActive);
      setEditingId(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update phrase');
    }
  };

  return (
    <div className="admin-content-sub-section">
      <div className="section-toolbar">
        <form onSubmit={handleAddSubmit} className="add-item-form">
          <input
            type="text"
            placeholder="New love phrase (any language)..."
            value={newPhrase}
            onChange={e => setNewPhrase(e.target.value)}
            disabled={isSubmitting}
            className="input-field phrase-input"
          />
          <input
            type="text"
            placeholder="Language"
            value={newLanguage}
            onChange={e => setNewLanguage(e.target.value)}
            disabled={isSubmitting}
            className="input-field lang-input"
          />
          <button type="submit" disabled={isSubmitting} className="action-btn action-btn-add">
            {isSubmitting ? 'Adding...' : 'Add Phrase'}
          </button>
        </form>

        {formError && <div className="form-error-inline">{formError}</div>}

        <div className="filters-row">
          <input
            type="search"
            placeholder="Search phrases..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="input-field search-input"
          />

          <select
            value={languageFilter}
            onChange={e => setLanguageFilter(e.target.value)}
            className="select-field"
          >
            <option value="all">All Languages ({phrases.length})</option>
            {availableLanguages.map(lang => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="admin-data-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Phrase</th>
              <th>Language</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPhrases.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-state">
                  No love phrases found matching your search.
                </td>
              </tr>
            ) : (
              filteredPhrases.map(item => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id} className={!item.isActive ? 'row-inactive' : ''}>
                    <td>
                      <button
                        type="button"
                        className={`status-pill ${item.isActive ? 'status-active' : 'status-disabled'}`}
                        onClick={() =>
                          onUpdatePhrase(item.id, item.phrase || '', item.language || 'English', !item.isActive)
                        }
                        title="Toggle active status"
                      >
                        {item.isActive ? 'Active' : 'Disabled'}
                      </button>
                    </td>

                    <td className="cell-text">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editPhrase}
                          onChange={e => setEditPhrase(e.target.value)}
                          className="input-field input-inline"
                        />
                      ) : (
                        <span className="phrase-display">{item.phrase}</span>
                      )}
                    </td>

                    <td className="cell-lang">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editLanguage}
                          onChange={e => setEditLanguage(e.target.value)}
                          className="input-field input-inline-sm"
                        />
                      ) : (
                        <span className="lang-tag">{item.language || 'English'}</span>
                      )}
                    </td>

                    <td className="cell-meta">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>

                    <td className="cell-actions">
                      {isEditing ? (
                        <div className="action-buttons-group">
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => handleSaveEdit(item.id, item.isActive)}
                          >
                            Save
                          </button>
                          <button type="button" className="btn-link btn-muted" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons-group">
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => startEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn-link btn-danger"
                            onClick={() => {
                              if (confirm(`Delete phrase "${item.phrase}"?`)) {
                                onDeletePhrase(item.id);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
